import { useEffect, useMemo } from 'react';
import { getKioskOptionInfo } from '@lib/api/v2/merchant';
import {
  allOpenDoor,
  innerKRestart,
  innerPRestart,
  innerUpdate,
  kRestart,
  multiOpenDoor,
  multiPcRestart,
  multiProgramRestart,
  openDoor,
  openDoorWithLocation,
  outerUpdate,
  pRestart,
  restartPCWithLocation,
  restartProgramWithLocation,
  updateKioskWithLocation,
} from '@lib/socket';
import useAppStore from '@store/useAppStore';
import { showKioskUpdateReceipt } from '@util/kioskUpdateReceipt';
import { getAllToken } from '@util/token';

interface KioskOption {
  id: number;
  location: DeviceLocation;
  number: number;
  vanType?: KioskVanType | null;
  payTid?: string | null;
  localTid?: string | null;
  kcpPosCode?: string | null;
  kcpReaderPort?: string | null;
  version?: string | null;
  deviceId?: string | null;
  kioskName?: string | null;
  deviceModel?: string | null;
  osVersion?: string | null;
  lastHeartbeatAt?: string | null;
}

interface UseKioskOptionReturn {
  kioskOptions: KioskOption[];
  hasKioskOptions: boolean;
  hasMultiKiosk: boolean;
  usingMultiKiosk: boolean;
  usingLegacyMultiKiosk: boolean;
  legacyMultiKioskFloor: number;
  handleOpenDoorLegacy: (number?: number) => void;
  handleOpenDoor: (number: number, location: DeviceLocation) => void;
  handleRestartProgramLegacy: (number?: number) => void;
  handleRestartPCLegacy: (number?: number) => void;
  handleRestartProgram: (number: number, location: DeviceLocation) => void;
  handleRestartPC: (number: number, location: DeviceLocation) => void;
  handleUpdateOuterLegacy: () => void;
  handleUpdateInnerLegacy: () => void;
  handleUpdate: (number: number, location: DeviceLocation) => void;
  handleOpenAllDoors: () => void;
}

/**
 * 키오스크 옵션 정보를 관리하는 커스텀 훅
 *
 * 레거시: merchantData.multiKioskFloor
 * 신규: v2/backoffice/merchant/kiosk/option/info
 *
 * 신규 API를 먼저 조회하여 정보가 있는지 확인
 * merchantToken이 유효할 때에만 작동
 *
 * 전역 상태(kioskOptionsState)를 사용하여 동일 merchantId에 대한 중복 API 요청 방지
 * merchantId가 변경되면 새로운 데이터를 가져옴
 */
const useKioskOption = (): UseKioskOptionReturn => {
  const {
    kioskOptions,
    setKioskOptions,
    kioskOptionsLoadedMerchantId: loadedMerchantId,
    setKioskOptionsLoadedMerchantId: setLoadedMerchantId,
    merchantData,
  } = useAppStore();
  const merchantId = merchantData?.id;

  useEffect(() => {
    const { merchantToken } = getAllToken();
    if (!merchantToken || !merchantId) return;

    // 이미 같은 merchantId로 로드했으면 API 요청을 건너뜀
    if (loadedMerchantId === merchantId) return;

    const fetchKioskOptions = async () => {
      try {
        const { data } = await getKioskOptionInfo();
        if (data && Array.isArray(data) && data.length > 0) setKioskOptions(data);
        else setKioskOptions([]);
        // API 요청 성공 후 현재 merchantId 저장
        setLoadedMerchantId(merchantId);
      } catch (err) {
        setKioskOptions([]);
      }
    };

    // merchantId가 변경되면 새로운 데이터를 가져옴
    fetchKioskOptions();
  }, [merchantId, loadedMerchantId, setKioskOptions, setLoadedMerchantId]);

  const hasMultiKiosk = useMemo(
    () => kioskOptions.length > 0 || !!merchantData?.multiKioskFloor,
    [kioskOptions, merchantData],
  );
  const usingMultiKiosk = useMemo(() => kioskOptions.length > 0, [kioskOptions]);
  const usingLegacyMultiKiosk = useMemo(() => {
    return !!merchantData?.multiKioskFloor && !usingMultiKiosk;
  }, [merchantData?.multiKioskFloor, usingMultiKiosk]);

  const handleOpenDoorLegacy = (number?: number) => {
    const multiKioskFloor = merchantData?.multiKioskFloor;
    if (!multiKioskFloor) return openDoor(merchantData.id);
    if (!number) return openDoor(merchantData.id);
    return multiOpenDoor(merchantData.id, number);
  };

  const handleOpenDoor = (number: number, location: DeviceLocation) => {
    openDoorWithLocation(merchantData.id, number, location);
  };

  const handleOpenAllDoors = () => {
    allOpenDoor(merchantData.id);
  };

  const handleRestartProgramLegacy = (number?: number) => {
    const multiKioskFloor = merchantData?.multiKioskFloor;
    if (!multiKioskFloor) return pRestart(merchantData.id);
    if (!number) return innerPRestart(merchantData.id);
    return multiProgramRestart(merchantData.id, number);
  };

  const handleRestartPCLegacy = (number?: number) => {
    const multiKioskFloor = merchantData?.multiKioskFloor;
    if (!multiKioskFloor) return kRestart(merchantData.id);
    if (!number) return innerKRestart(merchantData.id);
    return multiPcRestart(merchantData.id, number);
  };

  const handleRestartProgram = (number: number, location: DeviceLocation) => {
    restartProgramWithLocation(merchantData.id, number, location);
  };

  const handleRestartPC = (number: number, location: DeviceLocation) => {
    restartPCWithLocation(merchantData.id, number, location);
  };

  const handleUpdateOuterLegacy = () => {
    // 외부 키오스크 업데이트
    outerUpdate(merchantData.id);
  };

  const handleUpdateInnerLegacy = () => {
    // 내부 키오스크 업데이트
    innerUpdate(merchantData.id);
  };

  const handleUpdate = async (number: number, location: DeviceLocation) => {
    await showKioskUpdateReceipt(updateKioskWithLocation(merchantData.id, number, location));
  };

  return {
    kioskOptions,
    hasKioskOptions: kioskOptions.length > 0,
    hasMultiKiosk,
    usingMultiKiosk,
    usingLegacyMultiKiosk,
    legacyMultiKioskFloor: merchantData?.multiKioskFloor || 1,
    handleRestartProgramLegacy,
    handleRestartPCLegacy,
    handleOpenDoorLegacy,
    handleOpenDoor,
    handleOpenAllDoors,
    handleRestartProgram,
    handleRestartPC,
    handleUpdateOuterLegacy,
    handleUpdateInnerLegacy,
    handleUpdate,
  };
};

export default useKioskOption;
